import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import Reveal from "./Reveal";
import { MessageSquare, Zap, Clock, Shield, ArrowRight, Bot } from "lucide-react";

const ChatbotProductSection = () => {
  const features = [
    {
      icon: Clock,
      title: "24/7 verfügbar",
      desc: "Ihr Chatbot beantwortet Kundenanfragen rund um die Uhr – auch nachts und am Wochenende."
    },
    {
      icon: Zap,
      title: "Sofortige Antworten",
      desc: "Keine Wartezeiten mehr. Kunden erhalten sofort qualifizierte Antworten auf ihre Fragen."
    },
    {
      icon: Shield,
      title: "Einfache Integration",
      desc: "In wenigen Minuten auf Ihrer Website eingebunden – keine technischen Kenntnisse nötig."
    }
  ];

  return (
    <section className="relative py-20 md:py-28 overflow-hidden bg-muted/40">
      {/* Background decorations */}
      <div className="absolute inset-0 bg-dotgrid opacity-40 pointer-events-none" aria-hidden="true" />
      <div className="absolute -top-40 -right-40 w-80 h-80 bg-accent/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="mx-auto w-full max-w-7xl px-6 relative z-10">
        {/* Header */}
        <Reveal className="text-center mb-16">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 border border-primary/20 px-5 py-2.5 text-sm font-bold text-primary mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true"></span>
            NEU: KI-Chatbot Service
          </div>

          <h2 className="text-4xl md:text-5xl font-extrabold text-navy mb-6">
            Automatisieren Sie Ihren
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
              Kundenservice mit KI
            </span>
          </h2>

          <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Unser KI-Chatbot beantwortet Kundenanfragen auf Ihrer Website – intelligent,
            freundlich und immer verfügbar. Perfekt für kleine Unternehmen und Selbstständige.
          </p>
        </Reveal>

        {/* Features Grid */}
        <div className="grid gap-8 md:grid-cols-3 mb-12">
          {features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <Reveal
                key={i}
                delay={i * 90}
                className="group relative bg-card rounded-3xl p-8 shadow-soft border border-navy/10 transition-all duration-300 hover:shadow-lift hover:border-primary/35 hover:-translate-y-1.5"
              >
                {/* Leiterbahn-Punkt */}
                <span className="absolute top-6 right-6 h-2.5 w-2.5 rounded-full border-2 border-flame/45 transition-colors group-hover:border-flame" aria-hidden="true" />

                <div className="mb-6">
                  <div className="inline-flex rounded-2xl bg-navy p-4 shadow-soft transition-colors duration-300 group-hover:bg-primary">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-navy mb-3">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{feature.desc}</p>
              </Reveal>
            );
          })}
        </div>

        {/* CTA Box */}
        <Reveal className="bg-card rounded-[2rem] p-8 md:p-12 shadow-lift border border-navy/10 relative overflow-hidden">
          <div className="absolute inset-0 bg-circuit opacity-50 pointer-events-none" aria-hidden="true" />

          <div className="relative flex flex-col lg:flex-row items-center gap-8">
            {/* Left: Bot Illustration */}
            <div className="flex-shrink-0">
              <div className="relative">
                <div className="w-32 h-32 bg-gradient-to-br from-navy to-primary rounded-3xl flex items-center justify-center shadow-lift">
                  <Bot className="h-16 w-16 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-9 h-9 bg-gradient-flame rounded-full flex items-center justify-center shadow-soft">
                  <MessageSquare className="h-4 w-4 text-navy-deep" />
                </div>
              </div>
            </div>

            {/* Right: Content */}
            <div className="flex-1 text-center lg:text-left">
              <h3 className="text-2xl md:text-3xl font-bold text-navy mb-4">
                Starten Sie kostenlos – ohne Risiko
              </h3>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                Testen Sie unseren KI-Chatbot mit 10 kostenlosen Nachrichten pro Tag.
                Keine Kreditkarte erforderlich. Upgrade jederzeit möglich.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/chatbot-produkt">
                  <Button className="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg font-bold rounded-full shadow-soft transition-all hover:-translate-y-0.5">
                    Jetzt kostenlos testen
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/chatbot-produkt">
                  <Button variant="outline" className="border-2 border-navy/15 text-navy hover:bg-navy/5 hover:text-navy px-8 py-6 text-lg font-bold rounded-full transition-all">
                    Mehr erfahren
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Pricing Preview */}
          <div className="relative mt-10 pt-8 border-t border-border">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-3xl font-extrabold text-navy">0€</div>
                <div className="text-sm text-muted-foreground font-medium">Kostenlos starten</div>
              </div>
              <div>
                <div className="text-3xl font-extrabold text-navy">10</div>
                <div className="text-sm text-muted-foreground font-medium">Nachrichten/Tag gratis</div>
              </div>
              <div>
                <div className="text-3xl font-extrabold text-navy">5 Min</div>
                <div className="text-sm text-muted-foreground font-medium">Setup-Zeit</div>
              </div>
              <div>
                <div className="text-3xl font-extrabold text-navy">24/7</div>
                <div className="text-sm text-muted-foreground font-medium">Verfügbarkeit</div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default ChatbotProductSection;
